(function (global) {
  'use strict';

  var STORAGE_KEY = 'gcity:locale-switch-context:v1';
  var MAX_AGE_MS = 5 * 60 * 1000;

  function normalizeLocale(code) {
    var value = String(code || 'en').toLowerCase();
    if (value === 'zh-cn' || value === 'zh-hans' || value === 'zh-sg') return 'zh-CN';
    if (value === 'zh-tw') return 'zh-TW';
    if (value === 'zh-hk' || value === 'zh-hant' || value === 'zh-mo') return 'zh-HK';
    return 'en';
  }

  function normalizeRoot(root) {
    var value = String(root || '/').trim();
    if (!value || value === '/') return '';
    if (value.charAt(0) !== '/') value = '/' + value;
    return value.replace(/\/+$/, '');
  }

  function normalizePath(pathname) {
    var value = String(pathname || '/');
    if (value.charAt(0) !== '/') value = '/' + value;
    value = value.replace(/\/{2,}/g, '/');
    return value || '/';
  }

  function stripLocaleRoot(pathname, root) {
    var path = normalizePath(pathname);
    var localeRoot = normalizeRoot(root);
    if (!localeRoot) return path;
    if (path === localeRoot) return '/';
    if (path.indexOf(localeRoot + '/') === 0) return path.slice(localeRoot.length) || '/';
    return path;
  }

  function joinLocaleRoot(root, routePath) {
    var localeRoot = normalizeRoot(root);
    var path = normalizePath(routePath);
    if (!localeRoot) return path;
    if (path === '/') return localeRoot + '/';
    return localeRoot + path;
  }

  function safeStorage(storage) {
    if (storage && typeof storage.getItem === 'function' && typeof storage.setItem === 'function') return storage;
    try { return global.sessionStorage; } catch (_) { return null; }
  }

  function selectedLanguageCode(fallback) {
    var doc = global && global.document;
    if (doc && typeof doc.querySelector === 'function') {
      var selected = doc.querySelector('[data-global-localization-language]:checked');
      if (selected && selected.value) return selected.value;
    }
    return fallback;
  }

  function currentReturnTo(locationLike) {
    if (!locationLike) return '/';
    var doc = global && global.document;
    if (doc && doc.querySelector('[data-shell-page="error-404"]')) return '/';
    return normalizePath(locationLike.pathname || '/') + String(locationLike.search || '');
  }

  function syncLocalizationReturnTo(locationLike) {
    var doc = global && global.document;
    if (!doc || typeof doc.querySelectorAll !== 'function') return false;
    var current = locationLike || global.location;
    var root = doc.querySelector('[data-global-localization-surface]');
    var currentRoot = root ? root.getAttribute('data-locale-root') : '/';
    var value = currentReturnTo(current);
    if (value !== '/') {
      var suffix = String(current.search || '');
      value = stripLocaleRoot(current.pathname, currentRoot) + suffix;
    }
    var updated = false;
    Array.prototype.forEach.call(doc.querySelectorAll('form.header-localization-form'), function (form) {
      var input = form.querySelector('input[name="return_to"]');
      if (!input) return;
      input.value = value;
      updated = true;
    });
    return updated;
  }

  function capture(options) {
    options = options || {};
    var locationLike = options.location || global.location;
    if (!locationLike) return false;

    // Shopify can serialize the special `back` token as a locale-relative path
    // (for example /zh-hant/back). Normalize the hidden return_to field to the
    // actual current route immediately before the localization form submits.
    syncLocalizationReturnTo(locationLike);

    var storage = safeStorage(options.storage);
    if (!storage) return false;
    var record = {
      routePath: currentReturnTo(locationLike) === '/' ? '/' : stripLocaleRoot(locationLike.pathname, options.currentRoot),
      search: currentReturnTo(locationLike) === '/' ? '' : String(locationLike.search || ''),
      hash: currentReturnTo(locationLike) === '/' ? '' : String(locationLike.hash || ''),
      targetLocale: normalizeLocale(selectedLanguageCode(options.targetLocale)),
      createdAt: typeof options.now === 'number' ? options.now : Date.now()
    };
    try {
      storage.setItem(STORAGE_KEY, JSON.stringify(record));
      return true;
    } catch (_) {
      return false;
    }
  }

  function readRecord(storage) {
    if (!storage) return null;
    try {
      var raw = storage.getItem(STORAGE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch (_) {
      return null;
    }
  }

  function clear(storage) {
    if (!storage) return;
    try { storage.removeItem(STORAGE_KEY); } catch (_) {}
  }

  function expectedRelativeUrl(record, currentRoot) {
    if (!record || typeof record !== 'object') return '';
    var routePath = normalizePath(record.routePath || '/');
    var search = String(record.search || '');
    var hash = String(record.hash || '');
    if (search && search.charAt(0) !== '?') search = '?' + search;
    if (hash && hash.charAt(0) !== '#') hash = '#' + hash;
    return joinLocaleRoot(currentRoot, routePath) + search + hash;
  }

  function restore(options) {
    options = options || {};
    var storage = safeStorage(options.storage);
    if (!storage) return { restored: false, reason: 'storage_unavailable' };
    var record = readRecord(storage);
    if (!record) return { restored: false, reason: 'no_record' };

    var now = typeof options.now === 'number' ? options.now : Date.now();
    if (!record.createdAt || now - Number(record.createdAt) > MAX_AGE_MS) {
      clear(storage);
      return { restored: false, reason: 'expired' };
    }

    var currentLocale = normalizeLocale(options.currentLocale);
    if (normalizeLocale(record.targetLocale) !== currentLocale) {
      return { restored: false, reason: 'target_not_active' };
    }

    var locationLike = options.location || global.location;
    if (!locationLike) return { restored: false, reason: 'location_unavailable' };
    var expected = expectedRelativeUrl(record, options.currentRoot);
    var actual = normalizePath(locationLike.pathname) + String(locationLike.search || '') + String(locationLike.hash || '');

    clear(storage);
    if (actual === expected) return { restored: false, reason: 'already_preserved', expected: expected };

    if (typeof options.replace === 'function') {
      options.replace(expected);
      return { restored: true, expected: expected };
    }
    if (locationLike && typeof locationLike.replace === 'function') {
      locationLike.replace(expected);
      return { restored: true, expected: expected };
    }
    return { restored: false, reason: 'replace_unavailable', expected: expected };
  }

  var api = {
    STORAGE_KEY: STORAGE_KEY,
    MAX_AGE_MS: MAX_AGE_MS,
    normalizeLocale: normalizeLocale,
    normalizeRoot: normalizeRoot,
    normalizePath: normalizePath,
    stripLocaleRoot: stripLocaleRoot,
    joinLocaleRoot: joinLocaleRoot,
    selectedLanguageCode: selectedLanguageCode,
    currentReturnTo: currentReturnTo,
    syncLocalizationReturnTo: syncLocalizationReturnTo,
    expectedRelativeUrl: expectedRelativeUrl,
    capture: capture,
    restore: restore
  };

  global.GCityLocaleRouteContext = api;
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
})(typeof window !== 'undefined' ? window : globalThis);
